import React from 'react'
import { Icon } from '../../../atoms/Icon'
import { TransactionEvent as TransactionEventType } from './event.types'
import { formatSwissPrice, formatSwissPricePerSqm } from './formatUtils'
import {
  EventCard,
  EventHeader,
  EventTitle,
  EventIcon,
  EventContent,
  EventDetails,
  EventPrice,
  EventPriceSub,
  EventDescription,
  EventMeta,
  MetaItem,
  MetaIcon,
  EventFooter,
} from '../HistoryTab.styles'

interface TransactionEventProps {
  event: TransactionEventType
  translations?: {
    title?: string
    fields?: {
      buyers?: string
      sellers?: string
      transactionStep?: string
      properties?: string
      address?: string
      area?: string
      participants?: string
    }
  }
  formatPrice?: (price: number) => string
  formatPricePerSqm?: (price: number) => string
  formatDate?: (date: string) => string
}

export const TransactionEventComponent: React.FC<TransactionEventProps> = ({
  event,
  translations,
  formatPrice = formatSwissPrice,
  formatPricePerSqm = formatSwissPricePerSqm,
}) => {
  // Calculate total area from properties
  const totalArea =
    event.transaction_properties?.reduce((sum, prop) => {
      return sum + (prop.surface_area_total || 0)
    }, 0) || 0

  // Calculate price per sqm if we have both price and area
  const pricePerSqm =
    event.transaction_total_price && totalArea > 0
      ? event.transaction_total_price / totalArea
      : undefined

  // Get buyer and seller names from participants
  const buyers = event.transaction_participants
    ?.filter((p) => p.role === 'buyer')
    .map((p) => p.full_name)
    .join(', ')

  const sellers = event.transaction_participants
    ?.filter((p) => p.role === 'seller')
    .map((p) => p.full_name)
    .join(', ')

  // Get primary location
  const primaryLocation = event.transaction_properties?.[0]
  const locationText =
    primaryLocation && (primaryLocation.city_name || primaryLocation.npa_name)
      ? [primaryLocation.city_name, primaryLocation.npa_name]
          .filter(Boolean)
          .join(', ')
      : undefined

  return (
    <EventCard $type="transaction">
      <EventHeader>
        <EventTitle>
          <EventIcon>
            <Icon name="real-estate-transaction" size="small" />
          </EventIcon>
          {translations?.title || 'Property Transaction'}
        </EventTitle>
      </EventHeader>

      <EventContent>
        <EventDetails>
          {event.transaction_total_price &&
          event.transaction_total_price > 0 ? (
            <>
              <EventPrice>
                {formatPrice(event.transaction_total_price)}
              </EventPrice>
              {pricePerSqm && (
                <EventPriceSub>{formatPricePerSqm(pricePerSqm)}</EventPriceSub>
              )}
            </>
          ) : null}

          {/* Add description if available */}
          {event.transaction_step_text && (
            <EventDescription>{event.transaction_step_text}</EventDescription>
          )}

          <EventMeta>
            {buyers && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </MetaIcon>
                {translations?.fields?.buyers || 'Buyers'}: {buyers}
                {event.buyers_count > 1 && ` (${event.buyers_count})`}
              </MetaItem>
            )}

            {sellers && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
                </MetaIcon>
                {translations?.fields?.sellers || 'Sellers'}: {sellers}
                {event.sellers_count > 1 && ` (${event.sellers_count})`}
              </MetaItem>
            )}

            {event.transaction_step_name && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M11 17h2v-6h-2v6zm1-15C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zM11 9h2V7h-2v2z" />
                </MetaIcon>
                {translations?.fields?.transactionStep || 'Type'}:{' '}
                {event.transaction_step_name}
              </MetaItem>
            )}

            {locationText && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                </MetaIcon>
                {translations?.fields?.address || 'Location'}: {locationText}
              </MetaItem>
            )}

            {totalArea > 0 && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M4 4h16v16H4z" />
                </MetaIcon>
                {translations?.fields?.area || 'Area'}: {totalArea.toFixed(0)}{' '}
                m²
              </MetaItem>
            )}

            {event.properties_count > 0 && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </MetaIcon>
                {translations?.fields?.properties || 'Properties'}:{' '}
                {event.properties_count}
              </MetaItem>
            )}
          </EventMeta>
        </EventDetails>
      </EventContent>

      {/* Display official ID in footer if available */}
      {event.official_id && <EventFooter>ID: {event.official_id}</EventFooter>}
    </EventCard>
  )
}
